import { createContext, useContext, useState, useEffect } from 'react';
import ru from './ru.json';
import pl from './pl.json';
import uk from './uk.json';
import en from './en.json';

const translations = { ru, pl, uk, en };
const STORAGE_KEY = 'workifypl_lang';

const I18nContext = createContext();

function detectLanguage() {
  // 1. localStorage
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && translations[stored]) return stored;

  // 2. Telegram WebApp language
  const tgLang = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
  if (tgLang) {
    if (tgLang === 'uk' || tgLang === 'ua') return 'uk';
    if (translations[tgLang]) return tgLang;
  }

  // 3. Browser language
  const browserLang = navigator.language?.slice(0, 2);
  if (browserLang === 'uk' || browserLang === 'ua') return 'uk';
  if (translations[browserLang]) return browserLang;

  // 4. Default
  return 'ru';
}

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(detectLanguage);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[lang];

    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) break;
    }

    if (typeof value !== 'string') {
      // Fallback to Russian, then key
      let fallback = translations['ru'];
      for (const k of keys) {
        fallback = fallback?.[k];
        if (fallback === undefined) break;
      }
      if (Array.isArray(value) || Array.isArray(fallback)) {
        return value || fallback || [];
      }
      value = typeof fallback === 'string' ? fallback : key;
    }

    // Replace {{param}} placeholders
    if (params && typeof value === 'string') {
      Object.entries(params).forEach(([k, v]) => {
        value = value.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
      });
    }

    return value;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t, languages: Object.keys(translations) }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

// Language names for selector
export const languageNames = {
  ru: '🇷🇺 Русский',
  uk: '🇺🇦 Українська',
  pl: '🇵🇱 Polski',
  en: '🇬🇧 English',
};

export const languageFlags = {
  ru: '🇷🇺',
  uk: '🇺🇦',
  pl: '🇵🇱',
  en: '🇬🇧',
};

export default I18nContext;
